import {
  Contact,
  EventInput,
  ExternalPartner,
  LocationType,
  ShiftInput,
  SignupSummaryFrequency,
} from '@amzn/red-velvet-api';
import { TFunction } from 'i18next';
import { isValidEmail } from './validation';
import { FIELD_LIMITS } from './fieldConstraints';
import { getTextLength, stripHtml } from './htmlText';

export const GRANT_ID_REGEX = /^\d{4}VG-(AMER|APAC|EMEA)-\d{6}$/;

/** Returns true if the grant id is empty or matches the expected Benevity grant id format. */
export function isValidGrantIdFormat(grantId: string): boolean {
  return grantId === '' || GRANT_ID_REGEX.test(grantId);
}

export function validateEventDetails(eventInput: EventInput, t: TFunction): Record<string, string> {
  const errors: Record<string, string> = {};

  if (!eventInput.title?.trim()) {
    errors.eventTitle = t('error.eventTitleRequired');
  }
  if (!stripHtml(eventInput.description ?? '').trim()) {
    errors.eventDescription = t('error.eventDescriptionRequired');
  } else if (getTextLength(eventInput.description ?? '') > FIELD_LIMITS.EVENT_DESCRIPTION) {
    errors.eventDescription = t('error.inputTooLong', {
      maxLength: FIELD_LIMITS.EVENT_DESCRIPTION,
    });
  }
  if (!eventInput.shortDescription?.trim()) {
    errors.summarizedDescription = t('error.summarizedDescriptionRequired');
  }
  if (!eventInput.focusArea?.trim()) {
    errors.focusArea = t('error.focusAreaRequired');
  }
  if (!eventInput.primaryActivity?.trim()) {
    errors.primaryActivity = t('error.primaryActivityRequired');
  }
  if (eventInput.usedAciGrant === undefined) {
    errors.usedAciGrant = t('grant.questionRequired', { ns: 'translation' });
  }

  // Location-specific validation
  if (eventInput.location?.locationType === LocationType.ON_SITE) {
    if (!eventInput.location?.amazonBuilding?.trim()) {
      errors.building = t('error.buildingRequired');
    }
    if (!eventInput.location?.amazonBuildingRoom?.trim()) {
      errors.room = t('error.roomRequired');
    } else if ((eventInput.location?.amazonBuildingRoom?.length ?? 0) > FIELD_LIMITS.ROOM) {
      errors.room = t('error.inputTooLong', { maxLength: FIELD_LIMITS.ROOM });
    }
    if ((eventInput.location?.notes?.length ?? 0) > FIELD_LIMITS.LOCATION_NOTES) {
      errors.locationNotes = t('error.inputTooLong', { maxLength: FIELD_LIMITS.LOCATION_NOTES });
    }
  } else if (eventInput.location?.locationType === LocationType.OFF_SITE) {
    if (!eventInput.location?.address_string?.trim()) {
      errors.location = t('error.locationRequired');
    }
    if (!eventInput.location?.name?.trim()) {
      errors.locationName = t('error.locationNameRequired');
    } else if ((eventInput.location?.name?.length ?? 0) > FIELD_LIMITS.LOCATION_NAME) {
      errors.locationName = t('error.inputTooLong', { maxLength: FIELD_LIMITS.LOCATION_NAME });
    }
    if ((eventInput.location?.notes?.length ?? 0) > FIELD_LIMITS.LOCATION_NOTES) {
      errors.locationNotes = t('error.inputTooLong', { maxLength: FIELD_LIMITS.LOCATION_NOTES });
    }
  } else if (eventInput.location?.locationType === LocationType.VIRTUAL) {
    if ((eventInput.locationVirtualNotes?.length ?? 0) > FIELD_LIMITS.LOCATION_NOTES) {
      errors.locationVirtualNotes = t('error.inputTooLong', {
        maxLength: FIELD_LIMITS.LOCATION_NOTES,
      });
    }
  }

  // Waiver URL length validation
  if ((eventInput.waiverUrl?.length ?? 0) > FIELD_LIMITS.WAIVER_URL) {
    errors.waiverUrl = t('error.inputTooLong', { maxLength: FIELD_LIMITS.WAIVER_URL });
  }

  // ACI Grant specific validation
  if (eventInput.usedAciGrant === true) {
    if (!eventInput.grantId?.trim()) {
      errors.grantId = t('grant.idRequired', { ns: 'translation' });
    }
  }

  return errors;
}

export function validateShifts(shifts: ShiftInput[], t: TFunction): Record<string, string> {
  const errors: Record<string, string> = {};

  shifts.forEach((shift, index) => {
    const isScheduled = (shift.shiftTimingType ?? 'scheduled') === 'scheduled';

    if ((shift.name?.length ?? 0) > FIELD_LIMITS.SHIFT_NAME) {
      errors[`shift_${index}_name`] = t('error.inputTooLong', {
        maxLength: FIELD_LIMITS.SHIFT_NAME,
      });
    }

    if (!shift.startDateTime) {
      errors[`shift_${index}_date`] = t('error.shiftDateRequired');
      return;
    }

    if (isScheduled) {
      if (shift.endDateTime && shift.startDateTime.getTime() === shift.endDateTime.getTime()) {
        errors[`shift_${index}_startTime`] = t('error.shiftTimeRequired');
      } else if (shift.endDateTime && shift.endDateTime <= shift.startDateTime) {
        errors[`shift_${index}_endTime`] = t('error.shiftEndTimeBeforeStart');
      }
    } else {
      if (shift.endDateTime && shift.endDateTime <= shift.startDateTime) {
        const sameDay = shift.startDateTime.toDateString() === shift.endDateTime.toDateString();
        errors[sameDay ? `shift_${index}_endTime` : `shift_${index}_endDate`] = t(
          sameDay ? 'error.shiftEndTimeBeforeStart' : 'error.shiftEndDateBeforeStart'
        );
      }
      if (!shift.shiftLength || shift.shiftLength <= 0) {
        errors[`shift_${index}_shiftLength`] = t('error.shiftLengthRequired');
      } else if (shift.shiftLength > 720) {
        errors[`shift_${index}_shiftLength`] = t('error.shiftLengthExceeded');
      }
    }
  });

  return errors;
}

export function validateTeamAndPartnership(
  eventInput: EventInput,
  t: TFunction,
  options?: { coordinatorNotificationsEnabled?: boolean }
): Record<string, string> {
  const errors: Record<string, string> = {};

  if (eventInput.externalPlanningPartner === ExternalPartner.OTHER) {
    if (!eventInput.otherPartner?.trim()) {
      errors.otherPartnerName = t('error.otherPartnerRequired');
    } else if (eventInput.otherPartner.length > FIELD_LIMITS.OTHER_PARTNER_NAME) {
      errors.otherPartnerName = t('error.inputTooLong', {
        maxLength: FIELD_LIMITS.OTHER_PARTNER_NAME,
      });
    }
  }

  const validateContacts = (
    contacts: Contact[],
    prefix: string,
    opts: {
      requireName: boolean;
      requireEmail: boolean;
      requirePhone: boolean;
      nameErrorKey: string;
    }
  ) => {
    contacts.forEach((contact, i) => {
      if (opts.requireName && !contact.name?.trim() && !contact.alias?.trim()) {
        errors[`${prefix}_${i}_name`] = t(opts.nameErrorKey);
      } else if ((contact.name?.length ?? 0) > FIELD_LIMITS.CONTACT_NAME) {
        // Amazon contacts: name is autopopulated via alias search (PAPI), so inline validation is skipped.
        // External contacts: name is free-text, so inline validation + character counter is in ContactsComponent.
        errors[`${prefix}_${i}_name`] = t('error.inputTooLong', {
          maxLength: FIELD_LIMITS.CONTACT_NAME,
        });
      }
      if (opts.requireEmail && !contact.email?.trim()) {
        errors[`${prefix}_${i}_email`] = t('error.contactEmailRequired');
      } else if (contact.email && !isValidEmail(contact.email)) {
        errors[`${prefix}_${i}_email`] = t('error.invalidEmail');
      } else if ((contact.email?.length ?? 0) > FIELD_LIMITS.CONTACT_EMAIL) {
        errors[`${prefix}_${i}_email`] = t('error.inputTooLong', {
          maxLength: FIELD_LIMITS.CONTACT_EMAIL,
        });
      }
      if (opts.requirePhone && !contact.phone?.trim()) {
        errors[`${prefix}_${i}_phone`] = t('error.contactPhoneRequired');
      } else if ((contact.phone?.length ?? 0) > FIELD_LIMITS.CONTACT_PHONE) {
        errors[`${prefix}_${i}_phone`] = t('error.inputTooLong', {
          maxLength: FIELD_LIMITS.CONTACT_PHONE,
        });
      }
    });
  };

  validateContacts(eventInput.amazonContacts ?? [], 'amazonContact', {
    requireName: true,
    requireEmail: false,
    requirePhone: false,
    nameErrorKey: 'error.contactNameRequired',
  });
  validateContacts(eventInput.externalContacts ?? [], 'externalContact', {
    requireName: true,
    requireEmail: true,
    requirePhone: false,
    nameErrorKey: 'error.contactNameEmpty',
  });

  // Notification recipient validation (only when feature is enabled)
  if (options?.coordinatorNotificationsEnabled) {
    if (
      eventInput.notifyCoordinatorOnSignup &&
      (!eventInput.realTimeNotificationRecipients ||
        eventInput.realTimeNotificationRecipients.length === 0)
    ) {
      errors.realTimeRecipients = t('error.recipientsRequired');
    }
    const summaryEnabled =
      eventInput.signupSummaryFrequency !== undefined &&
      eventInput.signupSummaryFrequency !== SignupSummaryFrequency.NEVER;
    if (
      summaryEnabled &&
      (!eventInput.signupSummaryRecipients || eventInput.signupSummaryRecipients.length === 0)
    ) {
      errors.summaryRecipients = t('error.recipientsRequired');
    }
    if (
      eventInput.signupSummaryFrequency === SignupSummaryFrequency.WEEKLY &&
      (!eventInput.signupSummaryWeeklyDays || eventInput.signupSummaryWeeklyDays.length === 0)
    ) {
      errors.summaryWeeklyDays = t('error.weeklyDaysRequired');
    }
  }

  return errors;
}
